import React, { useId, useMemo } from 'react';
import { motion } from 'motion/react';
import logo from '../assets/logo.png';

interface VisualProps {
  scores: {
    responsibility: number;
    selftrust: number;
    care: number;
  };
  lightMode?: boolean;
  max?: number;
  showLogo?: boolean;
}

const COLORS = {
  trust: '#86bb93',
  responsibility: '#e3962f',
  care: '#57a3b0'
};
const GOLD = '#c8ab68';
const SQRT3_2 = 0.8660254;

const esc = (n: number) => Math.round(n * 100) / 100;
const clamp = (v: number, min: number, max: number) => Math.max(min, Math.min(max, v));
const fmt = (n: number) => (Math.round(n * 10) / 10).toString();

export const Visual: React.FC<VisualProps> = ({ scores, lightMode = false, max = 5, showLogo = true }) => {
  const rawId = useId();
  const uid = useMemo(() => `av-${rawId.replace(/:/g, '')}`, [rawId]);

  const cx = 300;
  const cy = 318;
  const D = 92;
  const size = 600;

  const rOf = (s: number) => {
    return clamp((140 / max) * s, (140 / max) * 1, 150);
  };

  const lobes = useMemo(() => {
    const trustScore = scores.selftrust || 0;
    const respScore = scores.responsibility || 0;
    const careScore = scores.care || 0;

    return [
      {
        key: 'trust',
        label: 'Self Trust',
        color: COLORS.trust,
        x: cx,
        y: cy - D,
        up: true,
        off: { dx: 0, dy: 0 },
        score: trustScore,
        r: rOf(trustScore)
      },
      {
        key: 'responsibility',
        label: 'Responsibility',
        color: COLORS.responsibility,
        x: cx - SQRT3_2 * D,
        y: cy + 0.5 * D,
        up: false,
        off: { dx: 0, dy: 0 },
        score: respScore,
        r: rOf(respScore)
      },
      {
        key: 'care',
        label: 'Care',
        color: COLORS.care,
        x: cx + SQRT3_2 * D,
        y: cy + 0.5 * D,
        up: false,
        off: { dx: 110, dy: -38 },
        score: careScore,
        r: rOf(careScore)
      }
    ];
  }, [scores, max]);

  const bgRect = !lightMode ? (
    <rect x="0" y="0" width={size} height={size} fill={`url(#${uid}-bg)`} />
  ) : null;

  const springConfig = { type: "spring", stiffness: 60, damping: 15 } as const;
  const blendMode = lightMode ? 'multiply' : 'screen';

  // Determine text coloring for light vs. dark mode
  const labelTextColor = lightMode ? '#334155' : '#cdd6da'; // Slate-700 vs. light-gray
  const labelSubTextColor = lightMode ? '#64748b' : '#5d666b'; // Slate-500 vs. dark-gray

  return (
    <svg 
      viewBox={`0 0 ${size} ${size}`} 
      xmlns="http://www.w3.org/2000/svg"
      className="w-full h-full max-w-[480px] max-h-[480px]"
      fontFamily="system-ui, -apple-system, sans-serif"
    >
      <defs>
        {/* Background gradient for dark mode */}
        <radialGradient id={`${uid}-bg`} cx="50%" cy="44%" r="62%">
          <stop offset="0%" stopColor="var(--bg-card)" />
          <stop offset="60%" stopColor="var(--bg-main)" />
          <stop offset="100%" stopColor="var(--bg-main)" />
        </radialGradient>

        {/* Dynamic lobe gradients using props/states */}
        {lobes.map(l => (
          <radialGradient key={l.key} id={`${uid}-${l.key}`} cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor={l.color} stopOpacity="0.42" />
            <stop offset="55%" stopColor={l.color} stopOpacity="0.14" />
            <stop offset="100%" stopColor={l.color} stopOpacity="0" />
          </radialGradient>
        ))}

        {/* Glow filter for thin borders */}
        <filter id={`${uid}-glow`} x="-40%" y="-40%" width="180%" height="180%">
          <feGaussianBlur stdDeviation="3.4" result="b" />
          <feMerge>
            <feMergeNode in="b" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>

        {/* Logo background gradient */}
        <radialGradient id={`${uid}-logobg`} cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor={lightMode ? '#ffffff' : 'var(--bg-main)'} stopOpacity="0.8" />
          <stop offset="68%" stopColor={lightMode ? '#ffffff' : 'var(--bg-main)'} stopOpacity="0.4" />
          <stop offset="100%" stopColor={lightMode ? '#ffffff' : 'var(--bg-main)'} stopOpacity="0" />
        </radialGradient>
      </defs>

      {bgRect}

      {/* Lobby Fills */}
      <g style={{ mixBlendMode: blendMode as any }}>
        {lobes.map(l => (
          <motion.circle
            key={`fill-${l.key}`}
            cx={esc(l.x)}
            cy={esc(l.y)}
            animate={{ r: l.r }}
            transition={springConfig}
            fill={`url(#${uid}-${l.key})`}
          />
        ))}
      </g>

      {/* Lobby Strokes with radial glow/halo */}
      <g style={{ mixBlendMode: blendMode as any }} filter={lightMode ? undefined : `url(#${uid}-glow)`}>
        {lobes.map(l => (
          <motion.circle
            key={`stroke-${l.key}`}
            cx={esc(l.x)}
            cy={esc(l.y)}
            animate={{ r: l.r }}
            transition={springConfig}
            fill="none"
            stroke={l.color}
            strokeWidth="1.5"
            strokeOpacity="0.92"
          />
        ))}
      </g>

      {/* Centred logo mark */}
      {showLogo && (
        <g>
          <circle cx="300" cy="318" r="46" fill={`url(#${uid}-logobg)`} />
          <image 
            href={logo} 
            x="275" 
            y="293" 
            width="50" 
            height="50" 
          />
        </g>
      )}

      {/* Labels & values text blocks */}
      <g>
        {lobes.map(l => {
          const px = l.x + l.off.dx;
          const pad = 34;
          const py = (l.up ? l.y - l.r - pad : l.y + l.r + pad) + l.off.dy;
          const ts = 12.5;
          const titleTs = ts * 1.5 * 0.84;
          const ls = esc(titleTs * 0.235);
          const sy = l.up ? py - ts * 2.0 : py + ts * 2.6;

          return (
            <g key={`label-group-${l.key}`}>
              {/* Category Title */}
              <text
                x={esc(px)}
                y={esc(py)}
                textAnchor="middle"
                fill={labelTextColor}
                fontSize={titleTs}
                letterSpacing={ls}
                fontWeight="500"
                style={{ textTransform: 'uppercase' }}
              >
                {l.label.toUpperCase()}
              </text>
              {/* Score text */}
              <text
                x={esc(px)}
                y={esc(sy)}
                textAnchor="middle"
                fill={l.color}
                fontSize={esc(ts * 2.4)}
                fontWeight="600"
                letterSpacing="1"
              >
                {fmt(l.score)}
                <tspan fill={lightMode ? '#4b5563' : '#94a3b8'} fontSize={esc(ts * 2.05 * 0.7)} fontWeight="500">
                  {` / ${fmt(max)}`}
                </tspan>
              </text>
            </g>
          );
        })}
      </g>
    </svg>
  );
};
